/**
 * Browser-side ceremony telemetry reporter.
 *
 * Fire-and-forget POST to `/api/client-telemetry`. Every call
 * resolves immediately and never throws — failure on the network
 * is irrecoverable from the client's perspective and the caller's
 * onboarding flow must not be blocked or aborted by it.
 *
 * # Transport choice
 *
 * The reporter uses `fetch({ keepalive: true, credentials: "omit" })`.
 *
 *   - `keepalive: true` keeps the request alive across navigation
 *     so events emitted as the user closes the tab still ship.
 *     This is the same primitive `navigator.sendBeacon` is built
 *     on — but `sendBeacon` does not support custom headers,
 *     and we need `X-Ceremony-ID` on every event so the server
 *     can join browser-side and server-side log lines for the
 *     same onboarding attempt.
 *   - `credentials: "omit"` is explicit: the endpoint is public
 *     and never branches on a session cookie. Sending cookies
 *     would invite cross-site rate-limit pollution from any 3p
 *     site whose users happen to share an IP with our user
 *     base.
 *   - `redirect: "error"` rejects redirects outright so a hostile
 *     LB or compromised intermediary cannot trick the browser
 *     into sending the body to a different origin.
 *   - `cache: "no-store"` is belt-and-suspenders against any
 *     intermediary trying to dedupe events.
 *
 * # Privacy
 *
 * The schema accepts no identifiers. The reporter additionally:
 *
 *   - Truncates `error_message` client-side to `MAX_ERROR_MESSAGE_LEN`
 *     before transmission. The frontend-side classifier already
 *     captures this from the original DOMException without
 *     redaction; a trailing email or token in a browser-supplied
 *     error string would only reach the network at this seam, so
 *     truncation here is the last client-side line of defense.
 *     The backend applies its own backstop redactor on receipt.
 *   - Does not log `error_message` content to console.
 */

import type { CapabilitySnapshot, UaHint } from "../device-capabilities";
import {
  capabilityToWire,
  type CeremonyTelemetryEventWire,
  type TelemetryOutcome,
  type TelemetryPhase,
  type TelemetryTransport,
} from "./event";

/** Hard cap on `error_message` length applied before transmission. */
export const MAX_ERROR_MESSAGE_LEN = 256;

/**
 * Configuration shared across every telemetry call from a single
 * onboarding attempt. The `baseUrl` is the API origin
 * (e.g. `https://tenant.us.example.com`); the `ceremonyId` is
 * a UUID minted once and stamped on every related call (also on
 * the X-Ceremony-ID header of /api/ceremony/v1 / /api/loginflow
 * requests).
 */
export interface CeremonyTelemetryConfig {
  baseUrl: string;
  ceremonyId?: string;
}

/**
 * The caller-facing event shape. CamelCase TS conventions; the
 * reporter serialises to snake_case wire format internally.
 */
export interface ReportCeremonyEventInput {
  phase: TelemetryPhase;
  outcome: TelemetryOutcome;
  code: string;
  errorName?: string;
  errorMessage?: string;
  durationMs?: number;
  transportAttempted?: TelemetryTransport;
  capability: CapabilitySnapshot;
  ua: UaHint;
  sessionId?: string;
}

/**
 * Fire a single telemetry event. Always returns immediately and
 * never throws. Failures are silently swallowed — the caller's
 * ceremony must not be blocked by telemetry.
 *
 * No-ops cleanly in three cases:
 *
 *   - SSR (`window` undefined). The reporter is a browser-side
 *     concern; a server render that calls it should produce no
 *     network activity.
 *   - `fetch` not available. Truly ancient browsers; the
 *     ceremony itself wouldn't work either, so dropping
 *     telemetry is the least of the user's problems.
 *   - `baseUrl` empty. Configuration didn't reach the caller;
 *     fall back to silence rather than POST to "/api/...".
 */
export function reportCeremonyEvent(
  cfg: CeremonyTelemetryConfig,
  event: ReportCeremonyEventInput,
): void {
  if (typeof window === "undefined") return;
  if (typeof fetch !== "function") return;
  if (!cfg.baseUrl) return;

  const wire = toWire(event);
  const body = JSON.stringify(wire);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (cfg.ceremonyId) {
    headers["X-Ceremony-ID"] = cfg.ceremonyId;
  }

  // The trailing `void`+`.catch(() => {})` ensures unhandled
  // rejections never propagate and never log to console as
  // unhandled — the caller must not see any telemetry failure
  // bleed into ceremony state.
  void fetch(`${cfg.baseUrl}/api/client-telemetry`, {
    method: "POST",
    body,
    headers,
    keepalive: true,
    credentials: "omit",
    redirect: "error",
    cache: "no-store",
  }).catch(() => {
    /* fire and forget */
  });
}

/**
 * reportEnvironmentInsight is the §E-4 helper for "we just
 * rendered a surface that blocks this user because their
 * environment is fragile / VDI / private mode / etc."
 *
 * Maps to a single `preflight` event with `outcome: "abort"`
 * (the user can't proceed via the normal path) and
 * `code: "env_<environment_class>"`. The closed-set
 * environment class lands in the same `code` slot the
 * server-side telemetry catalog already supports — no
 * schema change required.
 *
 * Call once per surface mount, NOT every render.
 */
export function reportEnvironmentInsight(
  cfg: CeremonyTelemetryConfig,
  capability: CapabilitySnapshot,
  ua: UaHint,
  environmentClass: string,
  surfaceName: string,
): void {
  reportCeremonyEvent(cfg, {
    phase: "preflight",
    outcome: "abort",
    // surfaceName is the dimension that distinguishes
    // "the fragile redirect fired" from "the no-working-
    // method screen fired"; without it dashboards would
    // collapse them into one bucket. Joined into the code
    // field because the wire format has only one code slot.
    code: `env_${environmentClass}__${surfaceName}`,
    capability,
    ua,
  });
}

/**
 * Mint a fresh ceremony correlation id. Uses `crypto.randomUUID`
 * when available (every modern browser), falls back to a
 * `Math.random`-based v4-ish string otherwise. The fallback is
 * NOT cryptographically random and is fine here because the
 * ceremony id is a join key, not a security token.
 */
export function createCeremonyId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 shape using Math.random. Acceptable because
  // ceremonyId carries no security meaning (the server treats
  // it as opaque metadata only).
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  const bytes = new Array<number>(16);
  for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant
  return (
    hex(bytes[0] ?? 0) +
    hex(bytes[1] ?? 0) +
    hex(bytes[2] ?? 0) +
    hex(bytes[3] ?? 0) +
    "-" +
    hex(bytes[4] ?? 0) +
    hex(bytes[5] ?? 0) +
    "-" +
    hex(bytes[6] ?? 0) +
    hex(bytes[7] ?? 0) +
    "-" +
    hex(bytes[8] ?? 0) +
    hex(bytes[9] ?? 0) +
    "-" +
    hex(bytes[10] ?? 0) +
    hex(bytes[11] ?? 0) +
    hex(bytes[12] ?? 0) +
    hex(bytes[13] ?? 0) +
    hex(bytes[14] ?? 0) +
    hex(bytes[15] ?? 0)
  );
}

// ── Internals ──────────────────────────────────────────────────────────

function toWire(event: ReportCeremonyEventInput): CeremonyTelemetryEventWire {
  const wire: CeremonyTelemetryEventWire = {
    phase: event.phase,
    outcome: event.outcome,
    code: event.code,
    capability: capabilityToWire(event.capability),
    ua: event.ua,
  };
  if (event.errorName !== undefined && event.errorName !== "") {
    wire.error_name = event.errorName;
  }
  if (event.errorMessage !== undefined && event.errorMessage !== "") {
    wire.error_message = truncate(event.errorMessage, MAX_ERROR_MESSAGE_LEN);
  }
  if (event.durationMs !== undefined) {
    wire.duration_ms = event.durationMs;
  }
  if (event.transportAttempted !== undefined) {
    wire.transport_attempted = event.transportAttempted;
  }
  if (event.sessionId !== undefined && event.sessionId !== "") {
    wire.session_id = event.sessionId;
  }
  return wire;
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}
